import React from 'react';
import { Card, CardContent } from './ui/card';
import { Star, ExternalLink } from 'lucide-react';
import { GoogleReviews } from './GoogleReviews';

export const Testimonials = () => {
  // Real Google Reviews - Replace these with actual reviews from your Google Business Profile
  // To add more reviews: Copy review text from Google Maps and add here
  const testimonials = [
    {
      name: 'Verified Customer',
      initial: 'A',
      role: 'Google Review',
      rating: 5,
      text: 'Excellent service! The driver was on time, professional, and the car was spotless. Used them for airport transfer and will definitely book again.',
      service: 'Airport Transfer',
      source: 'Google'
    },
    {
      name: 'Verified Customer',
      initial: 'R',
      role: 'Google Review',
      rating: 5,
      text: 'Very comfortable journey with a courteous driver. The pricing was transparent and reasonable. Highly recommend for outstation trips!',
      service: 'Outstation Trip',
      source: 'Google'
    },
    {
      name: 'Verified Customer',
      initial: 'S',
      role: 'Google Review',
      rating: 5,
      text: 'Always reliable, punctual, and professional. Been using for corporate travels. The best cab service in Bangalore!',
      service: 'Corporate Service',
      source: 'Google'
    },
    {
      name: 'Verified Customer',
      initial: 'M',
      role: 'Google Review',
      rating: 5,
      text: 'Used their hourly rental for a full day. Very convenient and the driver was very patient. Great value for money!',
      service: 'Hourly Rental',
      source: 'Google'
    },
    {
      name: 'Verified Customer',
      initial: 'K',
      role: 'Google Review',
      rating: 5,
      text: 'Perfect for sightseeing! The driver knew all the best spots and was very helpful. Clean car, smooth ride.',
      service: 'City Tour',
      source: 'Google'
    },
    {
      name: 'Verified Customer',
      initial: 'P',
      role: 'Google Review',
      rating: 5,
      text: 'My go-to service for all travel needs. Reliable, affordable, and always professional. Thank you for the great service!',
      service: 'Regular Customer',
      source: 'Google'
    }
  ];

  return (
    <section id="testimonials" className="py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em]">
              Customer Reviews
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-4 tracking-tight">
            What Our Customers Say
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-4">
            Real reviews from our satisfied customers
          </p>
          <a 
            href="https://www.google.com/maps/place/SG+Cabs/@12.9605611,77.7166139,18.31z/data=!4m14!1m7!3m6!1s0x3bae13a19e54156b:0xc35ed95745c1a8e6!2sSG+Cabs!8m2!3d12.960509!4d77.7174895!16s%2Fg%2F11ygq10rwk!3m5!1s0x3bae13a19e54156b:0xc35ed95745c1a8e6!8m2!3d12.960509!4d77.7174895!16s%2Fg%2F11ygq10rwk" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center text-[#d4af37] hover:text-[#c9a35c] font-semibold hover:underline"
          >
            <span>View all reviews on Google</span>
            <ExternalLink className="w-4 h-4 ml-2" />
          </a>
        </div>

        <GoogleReviews />

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <Card 
              key={testimonial.text} 
              className="lux-card rounded-2xl transition-all duration-300 hover:-translate-y-1"
            >
              <CardContent className="p-6">
                {/* Rating Stars */}
                <div className="flex items-center space-x-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={`star-${i}`} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-gray-300 mb-6 leading-relaxed text-sm">
                  "{testimonial.text}"
                </p>

                {/* Service Badge */}
                <div className="mb-4">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-[#d4af37]/10 text-[#d4af37] text-xs font-medium border border-[#d4af37]/20">
                    {testimonial.service}
                  </span>
                </div>

                {/* Customer Info */}
                <div className="flex items-center space-x-4 pt-4 border-t border-gray-200">
                  {/* Avatar with Initial */}
                  <div className="w-12 h-12 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 flex items-center justify-center text-[#d4af37] font-bold text-xl">
                    {testimonial.initial}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-semibold text-white">{testimonial.name}</h4>
                      {testimonial.source === 'Google' && (
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Indicators */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="text-center group">
            <div className="w-20 h-20 mx-auto mb-4 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <svg className="w-10 h-10 text-[#d4af37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="text-3xl font-bold text-[#d4af37] mb-2">10+ Years</div>
            <div className="text-gray-400 font-medium">Experience</div>
          </div>
          
          <div className="text-center group">
            <div className="w-20 h-20 mx-auto mb-4 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <svg className="w-10 h-10 text-[#d4af37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-3xl font-bold text-[#d4af37] mb-2">100%</div>
            <div className="text-gray-400 font-medium">Verified Drivers</div>
          </div>
          
          <div className="text-center group">
            <div className="w-20 h-20 mx-auto mb-4 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <svg className="w-10 h-10 text-[#d4af37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-3xl font-bold text-[#d4af37] mb-2">24/7</div>
            <div className="text-gray-400 font-medium">Available</div>
          </div>
          
          <div className="text-center group">
            <div className="w-20 h-20 mx-auto mb-4 bg-[#d4af37]/10 border border-[#d4af37]/30 rounded-full flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
              <svg className="w-10 h-10 text-[#d4af37]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
            <div className="text-3xl font-bold text-[#d4af37] mb-2">4.9★</div>
            <div className="text-gray-400 font-medium">Rating</div>
          </div>
        </div>
      </div>
    </section>
  );
};
